import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, Link, Outlet, useParams } from 'react-router-dom';
import { FileText, Menu, Plus, Clipboard, ChevronLeft, ChevronRight, List, ChevronDown, Settings} from 'lucide-react';
import supabase from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import SettingsModal from './SettingsModal';
import DeleteCaseModal from './DeleteCaseModal';
import ChatInterface from './ChatInterface';
import MobileLayout from './MobileLayout';
import '../styles/Layout.css';
import { groupChats } from '../utils';
import { toast } from 'react-hot-toast';
import { useAPI } from '../services/apiService';

const shouldShowSidebar = (pathname) => {
  return pathname.startsWith('/chat') || pathname.includes('/chronology');
};

const Layout = () => {
  const api = useAPI();
  const [selectedCase, setSelectedCase] = useState('');
  const [userCases, setUserCases] = useState([]);
  const [caseChats, setCaseChats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showDeleteCaseModal, setShowDeleteCaseModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [showFirmDropdown, setShowFirmDropdown] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showSpecialtiesMenu, setShowSpecialtiesMenu] = useState(false);
  const adminMenuRef = useRef(null);
  const [specialties, setSpecialties] = useState([]);
  const [specialtiesTimeout, setSpecialtiesTimeout] = useState(null);
  const [specialtiesLoading, setSpecialtiesLoading] = useState(false);
  const { caseId: urlCaseId } = useParams();

  const { 
    user, 
    firmContext, 
    userFirms, 
    switchFirm, 
    signOut, 
    hasPermission,
    isSessionValid,
    sessionStatus 
  } = useAuth();

  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const showSidebarForRoute = shouldShowSidebar(location.pathname);

  const fetchSpecialties = async () => {
    if (specialties.length > 0 || specialtiesLoading) return; // Don't fetch if we already have data or are loading
    
    try {
      setSpecialtiesLoading(true);
      const { data } = await api.get('/specialties');
      setSpecialties(data?.map(specialty => ({
        id: specialty.specialty_id,
        name: specialty.specialty_name,
        templateId: specialty.template_id
      })) || []);
    } catch (error) {
      console.error('Error fetching specialties:', error);
    } finally {
      setSpecialtiesLoading(false);
    }
  };

  useEffect(() => {
    if (user && firmContext) {
      fetchSpecialties();
    }
  }, [user, firmContext]);

  useEffect(() => {
    if (user && firmContext && !location.pathname.includes('/cases/')) {
      fetchUserCases();
    }
  }, [user, firmContext, location.pathname]);

  useEffect(() => {
    if (selectedCase) fetchCaseChats(selectedCase);
  }, [selectedCase]);

  useEffect(() => {
    const caseId = new URLSearchParams(location.search).get('caseId');
    const chatId = new URLSearchParams(location.search).get('chatId');
    if (chatId) fetchCaseForChat(chatId);
    else if (caseId) {
      setSelectedCase(caseId);
      fetchCaseChats(caseId);
    }
  }, [location]);

  useEffect(() => {
    if (sessionStatus === 'expired') {
      navigate('/login');
    } else if (sessionStatus === 'error') {
      toast.error('Having trouble connecting to the server. Will retry shortly...');
    }
  }, [sessionStatus, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.firm-dropdown')) {
        setShowFirmDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshCaseChats = useCallback(async (caseId) => {
    if (caseId) {
      await fetchCaseChats(caseId);
    }
  }, []);

  const fetchCaseForChat = async (chatId) => {
    try {
      const { data, error } = await supabase
        .from('chats')
        .select('case_id')
        .eq('chat_id', chatId)
        .single();

      if (error) throw error;

      if (data) {
        setSelectedCase(data.case_id.toString());
        fetchCaseChats(data.case_id);
      }
    } catch (error) {
      console.error('Error fetching case for chat:', error);
      setError(`Error fetching case for chat: ${error.message}`);
    }
  };

  const fetchUserCases = async () => {
    if (!firmContext) return;

    let query = supabase
      .from('cases')
      .select('*')
      .eq('is_deleted', false);

    const caseAccess = firmContext.permissions?.case_access || {};

    if (firmContext.permissions.super_admin) {
      // No additional filters needed for super admins
    } else if (caseAccess.all_within_firm) {
      // For admins and managers who can see all firm cases
      query = query.eq('firm_id', firmContext.firmId);
    } else {
      // For professionals (own cases) and analysts (assigned cases)
      const conditions = [];
      
      if (caseAccess.own) {
        conditions.push(`user_id.eq.${user.id}`);
      }
      
      if (caseAccess.assigned) {
        conditions.push(`case_id.in.(${
          supabase
            .from('case_analysts')
            .select('case_id')
            .eq('user_id', user.id)
            .toString()
        })`);
      }

      if (conditions.length > 0) {
        query = query.or(conditions.join(','));
      } else {
        // If no access levels are granted, return no cases
        setUserCases([]);
        return;
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user cases:', error);
      setError(`Error fetching cases: ${error.message}`);
    } else {
      setUserCases(data);
    }
  };

  const fetchCaseChats = async (caseId) => {
    const { data, error } = await supabase
      .from('chats')
      .select('chat_id, summary, case_id, created_at')
      .eq('case_id', caseId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching case chats:', error);
    } else {
      setCaseChats(data);
    }
  };

  const handleNewCase = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }
    try {
      setLoading(true);
      
      // Check current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. User is not authenticated.');
      }
      console.log('Active session found. User ID:', session.user.id);

      console.log('Creating new case for User ID:', user.id);
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .insert([{ case_name: 'New Case', user_id: user.id }])
        .select();

      if (caseError) throw caseError;

      const newCaseId = caseData[0].case_id;
      setUserCases(prevCases => [...prevCases, caseData[0]]);
      setSelectedCase(newCaseId);
      navigate(`/cases/${newCaseId}/files`);
    } catch (error) {
      console.error('Error creating new case:', error);
      setError(`Error creating new case: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = async (caseId) => {
    const activeCaseId = caseId || urlCaseId || selectedCase;
    if (!activeCaseId) {
      console.error('No case selected');
      return;
    }

    const { data, error } = await supabase
      .from('chats')
      .insert([{ case_id: activeCaseId, summary: 'New chat' }])
      .select();

    if (error) {
      console.error('Error creating new chat:', error);
    } else {
      const newChatId = data[0].chat_id;
      navigate(`/chat?chatId=${newChatId}`);
      fetchCaseChats(activeCaseId);
    }
  };

  const handleDeleteCase = async () => {
    if (!selectedCase) {
      console.error('No case selected');
      return;
    }
    setShowDeleteCaseModal(true);
  };

  const confirmDeleteCase = async () => {
    try {
      const { error } = await supabase
        .from('cases')
        .update({ is_deleted: true })
        .eq('case_id', selectedCase);

      if (error) throw error;

      // Update local state
      setUserCases(prevCases => prevCases.filter(c => c.case_id !== selectedCase));
      setSelectedCase('');
      setShowDeleteCaseModal(false);
      navigate('/');
    } catch (error) {
      console.error('Error deleting case:', error);
      setError(`Error deleting case: ${error.message}`);
    }
  };

  const handleCaseNameUpdate = (updatedCaseId, newCaseName) => {
    setUserCases(prevCases => 
      prevCases.map(c => 
        c.case_id === updatedCaseId 
          ? { ...c, case_name: newCaseName } 
          : c
      )
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFirmSwitch = async (firmId) => {
    await switchFirm(firmId);
    setShowUserMenu(false);
  };

  const handleSpecialtiesMouseEnter = () => {
    if (specialtiesTimeout) {
      clearTimeout(specialtiesTimeout);
    }
    setShowSpecialtiesMenu(true);
  };

  const handleSpecialtiesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowSpecialtiesMenu(false);
    }, 300); // 300ms delay before hiding
    setSpecialtiesTimeout(timeout);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (specialtiesTimeout) {
        clearTimeout(specialtiesTimeout);
      }
    };
  }, [specialtiesTimeout]);

  const handleAdminMenuClick = async () => {
    setShowAdminMenu(!showAdminMenu);
    if (!showAdminMenu) {
      // Only fetch specialties when opening the menu
      fetchSpecialties();
    }
  };

  const renderUserMenu = () => (
    <div className="user-menu" ref={userMenuRef}>
      <div onClick={() => setShowUserMenu(!showUserMenu)}>
        <span>{user.email}</span>
        {sessionStatus === 'refreshing' && (
          <span className="text-gray-400 text-sm ml-2">(Refreshing...)</span>
        )}
        <Menu size={20} />
      </div>
      {showUserMenu && (
        <div className="user-menu-dropdown">
          <button onClick={() => { setShowSettingsModal(true); setShowUserMenu(false); }}>
            User Settings
          </button>
          <button onClick={() => { signOut(); setShowUserMenu(false); }}>
            Logout
          </button>
          
          {firmContext && userFirms.length > 0 && (
            <div className="firm-row">
              <span>Firm:</span>
              <div className="firm-select">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFirmDropdown(!showFirmDropdown);
                  }}
                  className="firm-dropdown-button"
                >
                  {firmContext.firmName}
                  <ChevronDown size={14} />
                </button>
                
                {showFirmDropdown && (
                  <div className="firm-dropdown-menu">
                    {userFirms.map(firm => (
                      <button
                        key={firm.firmId}
                        onClick={() => {
                          handleFirmSwitch(firm.firmId);
                          setShowFirmDropdown(false);
                        }}
                        className={`firm-option ${firm.firmId === firmContext.firmId ? 'active' : ''}`}
                      >
                        {firm.firmName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  useEffect(() => {
    if (urlCaseId) {
      setSelectedCase(urlCaseId);
    }
  }, [urlCaseId]);

  if (isMobile) {
    return (
      <MobileLayout
        userCases={userCases}
        selectedCase={selectedCase}
        setSelectedCase={setSelectedCase}
        handleNewCase={handleNewCase}
        handleNewChat={handleNewChat}
        caseChats={caseChats}
        refreshCaseChats={refreshCaseChats}
      />
    );
  }

  return (
    <div className="layout">
      <nav className="top-nav">
        <div className="nav-left">
          <div className="logo" onClick={() => navigate('/cases')} role="button">
            <img src="/logo_dark.png" alt="Paribus Logo" />
            <h1 className="header-title">
              <span>P</span>
              <span>a</span>
              <span>r</span>
              <span>i</span>
              <span>b</span>
              <span>u</span>
              <span>s</span>
            </h1>
          </div>
        </div>
        <div className="nav-right">
          {(hasPermission('organization', 'manage_users') || hasPermission('super_admin')) && (
            <div className="admin-menu" ref={adminMenuRef}>
              <button
                onClick={handleAdminMenuClick}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-600 hover:text-gray-700 focus:outline-none transition"
              >
                <Settings className="h-5 w-5 mr-1" />
                Admin
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              {showAdminMenu && (
                <div className="admin-menu-dropdown">
                  <Link
                    to="/physicians"
                    className="admin-menu-item"
                    onClick={() => setShowAdminMenu(false)}
                  >
                    Physicians
                  </Link>
                  <Link
                    to="/firm-settings"
                    className="admin-menu-item"
                    onClick={() => setShowAdminMenu(false)}
                  >
                    Firm Settings
                  </Link>
                  <div 
                    className="admin-menu-item specialties-menu"
                    onMouseEnter={handleSpecialtiesMouseEnter}
                    onMouseLeave={handleSpecialtiesMouseLeave}
                  >
                    <div className="flex items-center justify-between">
                      <span>Specialties</span>
                      <ChevronRight className="h-4 w-4" />
                    </div>
                    {showSpecialtiesMenu && (
                      <div 
                        className="specialties-submenu"
                        onMouseEnter={handleSpecialtiesMouseEnter}
                        onMouseLeave={handleSpecialtiesMouseLeave}
                      >
                        {specialtiesLoading ? (
                          <div className="admin-menu-item">Loading...</div>
                        ) : specialties.length > 0 ? (
                          specialties.map(specialty => (
                            <Link
                              key={specialty.id}
                              to={`/templates/${specialty.templateId}`}
                              state={{ 
                                templateId: specialty.templateId,
                                type: 'specialty',
                                specialtyId: specialty.id,
                                specialtyName: specialty.name
                              }}
                              className="admin-menu-item"
                              onClick={() => {
                                setShowAdminMenu(false);
                                setShowSpecialtiesMenu(false);
                              }}
                            >
                              {specialty.name}
                            </Link>
                          ))
                        ) : (
                          <div className="admin-menu-item">No specialties found</div>
                        )}
                      </div>
                    )}
                  </div>
                  <Link
                    to="/users"
                    className="admin-menu-item"
                    onClick={() => setShowAdminMenu(false)}
                  >
                    Users
                  </Link>
                </div>
              )}
            </div>
          )}
          <button onClick={() => navigate('/cases')} className="h-9 px-4 bg-green-200 text-green-800 text-sm rounded-md hover:bg-green-300 transition-colors">
            Cases
          </button>
          {user && renderUserMenu()}
        </div>
      </nav>

      <div className="main-content">
        {showSidebarForRoute && (
          <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-top">
              {selectedCase && (
                <>
                  <button 
                    onClick={() => navigate(`/cases/${selectedCase}/chronology`)} 
                    className="medical-chronology-btn"
                  >
                    Medical Chronology
                  </button>
                </>
              )}
              <button 
                onClick={() => handleNewChat(selectedCase)} 
                className="new-chat-btn"
                disabled={isHomePage}
              >
                + New Question
              </button>
              {!isHomePage && (
                <h2>Past Questions</h2>
              )}
            </div>

            {!isHomePage && (
              <div className="chat-history">
                {Object.entries(groupChats(caseChats)).map(([groupName, chats]) => (
                  chats.length > 0 && (
                    <div key={groupName} className="chat-group">
                      <h3 className="group-header">{groupName}</h3>
                      <ul>
                        {chats.map((chat) => (
                          <li key={chat.chat_id}>
                            <button onClick={() => navigate(`/chat?chatId=${chat.chat_id}`)}>
                              <span>{chat.summary}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            )}

            <button 
              className="toggle-sidebar-btn"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            >
              {isSidebarCollapsed ? (
                <>Ask <ChevronRight size={16} /></>
              ) : (
                <ChevronLeft size={16} className="collapse-icon" />
              )}
            </button>
          </div>
        )}

        <div className={`content ${showSidebarForRoute ? 'with-sidebar' : ''}`}>
          {location.pathname.startsWith('/chat') ? (
            <ChatInterface onSummaryUpdate={refreshCaseChats} />
          ) : (
            <Outlet context={{ onCaseNameUpdate: handleCaseNameUpdate }} />
          )}
        </div>
      </div>

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      <DeleteCaseModal
        isOpen={showDeleteCaseModal}
        onClose={() => setShowDeleteCaseModal(false)}
        onConfirm={confirmDeleteCase}
      />
    </div>
  );
};

export default Layout;